import React, { useEffect, useCallback, useState, useRef } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useSetRecoilState, useRecoilValue, useRecoilState, useRecoilCallback } from 'recoil';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_userAddedPlaces, PlaceDetails, atom_selectedUserPlace } from '@app/shared/state/user-added-stops.atom';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { useTranslation } from 'react-i18next';

import { service_tourService } from '@app/shared/services/tours.service';
import { useResizablePanel } from '@app/shared/hooks/useResizablePanel';
import { PlaceSearchInline } from '@app/shared/components/add-a-place/PlaceSearchInline';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface MobileDropAPinProps {
  onClose: () => void;
  isRTL: boolean;
  bottomBarHeight: number;
}

const THEME_COLOR = '#D0552E';
const MAX_DESCRIPTION_LENGTH = 100;

export const MobileDropAPin: React.FC<MobileDropAPinProps> = ({
  onClose,
  isRTL,
  bottomBarHeight,
}) => {
  const { t, i18n } = useTranslation();
  const setListMode = useSetRecoilState(atom_listModeState);
  const [dropPinMode, setDropPinMode] = useRecoilState(atom_dropPinMode);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const activeTour = useRecoilValue(atom_tourState);
  const [userAddedPlaces, setUserAddedPlaces] = useRecoilState(atom_userAddedPlaces);
  const [selectedUserPlace, setSelectedUserPlace] = useRecoilState(atom_selectedUserPlace);
  const map = useRecoilValue(atom_mapRef);

  // Pin location state
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Details state
  const [placeName, setPlaceName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>(['Establishment', 'Park']);
  const [placeType, setPlaceType] = useState<'dropped_pin' | 'search_place'>('dropped_pin');

  // Ref to measure content height
  const contentBoxRef = useRef<HTMLDivElement>(null);

  // Min constraint
  const minHeight = 150;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxAvailableHeight = Math.floor((viewportHeight - bottomBarHeight) * 0.8);

  const [isInitialized, setIsInitialized] = useState(false);

  const { currentHeight, setCurrentHeight, isDragging, dragHandleProps, containerTransitionStyle, startExit } = useResizablePanel({
    minHeight,
    maxHeight: maxAvailableHeight,
    initialHeight: 0,
  });

  // Lazy getter for geolocation
  const getGeolocation = useRecoilCallback(({ snapshot }) => () => {
    return snapshot.getLoadable(atom_geolocationState).getValue();
  }, []);

  // Get first stop location as fallback
  const firstStop = activeTour?.stops?.[0];
  const firstStopLocation: [number, number] | null = firstStop?.stop_location
    ? [firstStop.stop_location.latitude, firstStop.stop_location.longitude]
    : null;

  // Default center: user's geolocation or first tour stop
  const getDefaultCenter = useCallback((): [number, number] => {
    // If editing existing place, use its location
    if (selectedUserPlace?.geometry?.location) {
      return [selectedUserPlace.geometry.location.lat, selectedUserPlace.geometry.location.lng];
    }

    const geolocation = getGeolocation();
    if (geolocation.latitude && geolocation.longitude) {
      return [geolocation.latitude, geolocation.longitude];
    }

    return firstStopLocation || [32.0853, 34.7818];
  }, [getGeolocation, firstStopLocation, selectedUserPlace]);

  const defaultCenter = getDefaultCenter();

  // Set pin location from default center on mount
  useEffect(() => {
    if (!pinLocation && !selectedUserPlace) {
      setPinLocation({ lat: defaultCenter[0], lng: defaultCenter[1] });
    }
  }, [defaultCenter, pinLocation, selectedUserPlace]);

  // Measure content and set initial height to content size (capped at max)
  // Wait for pinLocation to be set before measuring (content is empty without it)
  useEffect(() => {
    if (!isDesktop && contentBoxRef.current && !isInitialized && pinLocation) {
      const measureHeight = () => {
        if (contentBoxRef.current) {
          // Measure actual content height
          const contentHeight = contentBoxRef.current.scrollHeight;
          // Use content height but cap at max available
          const initialHeight = Math.min(contentHeight, maxAvailableHeight);
          const safeHeight = Math.max(initialHeight, minHeight);

          setCurrentHeight(safeHeight);
          setIsInitialized(true);
        }
      };
      requestAnimationFrame(measureHeight);
    }
  }, [isDesktop, isInitialized, maxAvailableHeight, minHeight, pinLocation]);

  // Load existing place data if editing
  useEffect(() => {
    if (selectedUserPlace) {
      setPlaceName(selectedUserPlace.name || '');
      setDescription(selectedUserPlace.description || '');
      setTags(selectedUserPlace.tags || ['Establishment', 'Park']);
      setPlaceType(selectedUserPlace.placeType === 'search_place' ? 'search_place' : 'dropped_pin');
      if (selectedUserPlace.geometry?.location) {
        setPinLocation({
          lat: selectedUserPlace.geometry.location.lat,
          lng: selectedUserPlace.geometry.location.lng,
        });
      }
    }
  }, [selectedUserPlace]);

  // Load pin location and search name from drop pin mode when coming from main map positioning
  useEffect(() => {
    if (dropPinMode.step === 'naming' && dropPinMode.pinLocation) {
      setPinLocation({
        lat: dropPinMode.pinLocation.lat,
        lng: dropPinMode.pinLocation.lng,
      });
      if (dropPinMode.searchName && !selectedUserPlace) {
        setPlaceName(dropPinMode.searchName);
        setPlaceType('search_place');
      }
    }
  }, [dropPinMode.step, dropPinMode.pinLocation, dropPinMode.searchName, selectedUserPlace]);

  // Close and return to default list mode
  const handleClose = (removeTempPlace = true) => {
    startExit(() => {
      const tempId = dropPinMode.tempPlaceId;
      const tourId = activeTour?.id || '';
      const existingPlace = tempId && tourId ? (userAddedPlaces[tourId] || []).find((p: PlaceDetails) => p.place_id === tempId) : null;
      const isEditingExisting = existingPlace && (existingPlace as any).updatedAt;
      if (isEditingExisting && activeTour?.id) {
        // Editing a saved place — save updated location to localStorage + backend
        const tourId = activeTour.id;
        const place = (userAddedPlaces[tourId] || []).find((p: PlaceDetails) => p.place_id === tempId);
        localStorage.setItem('userAddedPlaces', JSON.stringify(userAddedPlaces));
        if (place) {
          const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
          if (uID) {
            service_tourService.saveUserAddedStop(uID, tourId, place);
          }
        }
      } else if (removeTempPlace && tempId && activeTour?.id) {
        // New temp place — remove it if cancelling
        const tourId = activeTour.id;
        const currentPlaces = userAddedPlaces[tourId] || [];
        const filtered = currentPlaces.filter((p: PlaceDetails) => p.place_id !== tempId);
        const updatedPlaces = { ...userAddedPlaces, [tourId]: filtered };
        setUserAddedPlaces(updatedPlaces);
        localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));
      }
      // Reset drop pin mode (important for main map)
      setDropPinMode({ isActive: false, step: null, pinLocation: null });
      // Reset selected user place so next "Add Place" starts fresh
      setSelectedUserPlace(null);
      onClose();
    });
  };

  // Prevent map interaction handler
  const handlePreventMapInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  // Handle edit location - go back to positioning on main map
  const handleEditLocation = () => {
    // Track which place to hide during positioning (works for both temp and saved places)
    const placeId = selectedUserPlace?.place_id || dropPinMode.tempPlaceId || null;
    setListMode(null);
    setDropPinMode({
      isActive: true,
      step: 'positioning',
      pinLocation: pinLocation,
      tempPlaceId: placeId,
    });
  };

  // Handle search place selected - update pin location and name from search result
  const handleSearchPlaceSelected = (details: PlaceDetails) => {
    setPinLocation({
      lat: details.geometry.location.lat,
      lng: details.geometry.location.lng,
    });
    setPlaceName(details.name || '');
    setPlaceType('search_place');

    // Fly map to searched location, offset so marker appears above the panel
    if (map) {
      const lat = details.geometry.location.lat;
      const lng = details.geometry.location.lng;
      const zoom = 17;
      const PAN_HEIGHT = 300;
      const targetPoint = map.project([lat, lng], zoom);
      targetPoint.y += PAN_HEIGHT / 2;
      const offsetLatLng = map.unproject(targetPoint, zoom);
      map.flyTo(offsetLatLng, zoom, { duration: 1.5 });
    }
  };

  // Handle delete place
  const handleDeletePlace = async () => {
    if (!selectedUserPlace?.place_id || !activeTour?.id) return;

    const tourId = activeTour.id;
    const currentTourPlaces = userAddedPlaces[tourId] || [];
    const updatedPlaces = {
      ...userAddedPlaces,
      [tourId]: currentTourPlaces.filter((p: PlaceDetails) => p.place_id !== selectedUserPlace.place_id),
    };

    setUserAddedPlaces(updatedPlaces);
    localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));

    // Sync delete to backend
    const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
    if (uID) {
      service_tourService.deleteUserAddedStop(uID, tourId, selectedUserPlace.place_id);
    }

    setSelectedUserPlace(null);
    handleClose(false);
  };

  // Handle save place
  const handleSavePlace = async () => {
    if (!pinLocation || !activeTour?.id || !placeName.trim()) return;

    const tourId = activeTour.id;
    const currentTourPlaces = userAddedPlaces[tourId] || [];

    const isEditing = selectedUserPlace && selectedUserPlace.place_id;
    const tempId = dropPinMode.tempPlaceId;

    const placeToSave: PlaceDetails = {
      place_id: isEditing ? selectedUserPlace.place_id : (tempId || crypto.randomUUID()),
      name: placeName.trim(),
      description: description.trim(),
      geometry: {
        location: {
          lat: pinLocation.lat,
          lng: pinLocation.lng,
        },
      },
      icon: 'user',
      tags: tags,
      placeType: placeType,
    };

    const updatedPlaces = (isEditing || tempId)
      ? {
        ...userAddedPlaces,
        [tourId]: currentTourPlaces.map((p: PlaceDetails) =>
          p.place_id === (isEditing ? selectedUserPlace.place_id : tempId) ? placeToSave : p
        ),
      }
      : {
        ...userAddedPlaces,
        [tourId]: [...currentTourPlaces, placeToSave],
      };

    setUserAddedPlaces(updatedPlaces);
    localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));

    // Sync to backend
    const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
    if (uID) {
      service_tourService.saveUserAddedStop(uID, tourId, placeToSave);
    }

    handleClose(false);
  };

  // Render details view
  const renderDetailsView = () => {
    if (!pinLocation) return null;

    return (
      <>
        {/* Header */}
        {/* Header - edit button and close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 3.5,
            pb: 1.5,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Box
            onClick={handleEditLocation}
            sx={{
              bgcolor: '#48526B',
              borderRadius: '20px',
              px: 1.5,
              py: 0.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': { bgcolor: '#3a4259' },
            }}
          >
            <i className="fa-solid fa-pen" style={{ fontSize: 12, color: 'white' }} />
            <Typography sx={{ fontSize: 13, color: 'white' }}>
              {t('edit_location') || 'Edit location'}
            </Typography>
          </Box>
          <Box
            onClick={() => handleClose()}
            sx={{
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: 18, color: '#666' }} />
          </Box>
        </Box>

        {/* Content - scrollable */}
        <Box sx={{ p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Place name */}
          <Box>
            <TextField
              fullWidth
              value={placeName}
              onChange={(e) => { if (e.target.value.length <= 50) setPlaceName(e.target.value); }}
              inputProps={{ maxLength: 50 }}
              variant="standard"
              placeholder={t('place_name') || 'Place name'}
              autoComplete="off"
              spellCheck={false}
              sx={{
                '& .MuiInput-root': {
                  fontSize: 16,
                  fontWeight: 500,
                  '&:before': { borderBottom: '1px solid #e0e0e0' },
                  '&:after': { borderBottom: `2px solid ${THEME_COLOR}` },
                },
              }}
            />
          </Box>

          {/* Description */}
          <Box>
            <TextField
              fullWidth
              value={description}
              placeholder={t('description') || 'Description'}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
                  setDescription(e.target.value);
                }
              }}
              variant="standard"
              multiline
              minRows={1}
              sx={{
                '& .MuiInput-root': {
                  fontSize: 14,
                  '&:before': { borderBottom: '1px solid #e0e0e0' },
                  '&:after': { borderBottom: `2px solid ${THEME_COLOR}` },
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: '#999' }}>
                {description.length}/{MAX_DESCRIPTION_LENGTH}
              </Typography>
            </Box>
          </Box>

          {/* Tags */}
          {/* <Box>
            <Typography sx={{ fontSize: 14, color: '#666', mb: 1 }}>
              {t('tags') || 'Tags'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, direction: 'ltr' }}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  onDelete={() => setTags(tags.filter((_, i) => i !== index))}
                  sx={{
                    bgcolor: '#48526B',
                    color: 'white',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': { color: 'white' },
                    },
                  }}
                />
              ))}
              <Button
                size="small"
                startIcon={<i className="fa-solid fa-plus" style={{ fontSize: 10 }} />}
                onClick={() => {
                  const newTag = prompt(t('enter_tag') || 'Enter tag name');
                  if (newTag?.trim()) {
                    setTags([...tags, newTag.trim()]);
                  }
                }}
                sx={{
                  textTransform: 'none',
                  color: '#48526B',
                  backgroundColor: '#ECEEF2',
                  border: '1px dashed #ccc',
                  borderRadius: '16px',
                  px: 1.5,
                  fontSize: 13,
                  '&:hover': { bgcolor: '#ECEEF2', borderColor: '#999' },
                }}
              >
                {t('add') || 'Add'}
              </Button>
            </Box>
          </Box> */}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            p: 2,
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                py: 1,
                borderColor: '#ddd',
                color: '#666',
                '&:hover': { borderColor: '#ccc', bgcolor: '#f9f9f9' },
              }}
            >
              {t('cancel') || 'Cancel'}
            </Button> */}
            {selectedUserPlace && selectedUserPlace.place_id && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleDeletePlace}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  py: 1,
                  borderColor: '#48526B',
                  '&:hover': { borderColor: '#3a4259', bgcolor: 'rgba(72,82,107,0.08)' },
                }}
              >
                {t('delete') || 'Delete'}
              </Button>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSavePlace}
              disabled={!placeName.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                py: 1,
                bgcolor: '#48526B',
                '&:hover': { bgcolor: '#3a4259' },
                '&.Mui-disabled': { bgcolor: '#ccc', color: 'white' },
              }}
            >
              {t('save_place') || 'Save Place'}
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  // Main container
  return (
    <Box
      ref={contentBoxRef}
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        boxShadow: '0px -4px 10px 0px rgba(0,0,0,0.15)',
        backgroundColor: 'white',
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: isRTL ? 'right' : 'left',
        position: 'fixed',
        zIndex: Z_INDEX.modal,
        borderRadius: '20px 20px 0px 0px',
        bottom: bottomBarHeight,
        left: 0,
        right: 0,
        touchAction: 'none',
        pointerEvents: 'auto',
        ...containerTransitionStyle,
      }}
      onClick={handlePreventMapInteraction}
      onMouseDown={handlePreventMapInteraction}
      onMouseMove={handlePreventMapInteraction}
    >
      {/* Drag Handle */}
      {!isDesktop && isInitialized && (
        <Box
          {...dragHandleProps}
          sx={{
            position: 'relative',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ns-resize',
            touchAction: 'none',
            padding: '12px 50px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(95,93,92,1)',
              height: '6px',
              width: '60px',
              borderRadius: '999px',
            }}
          />
        </Box>
      )}
      {renderDetailsView()}
    </Box>
  );
};

export default MobileDropAPin;
